import { computed, ref, watch, onMounted } from 'vue'
import { types } from '../components/notification/Notification.config'

export function messageMixin({ props, emit }) {
  const isActive = ref(props.active)
  const timer = ref(null)

  /**
   * @return {String|Null}
   * @description Icon name (MDI) based on type.
   */
  const computedIcon = computed(() => {
    if (props.icon) {
      return props.icon
    }
    if (props.large) {
      switch (props.type) {
        case 'info':
          return 'information'
        case 'success':
          return 'checkmark'
        case 'warning':
          return 'alert'
        case 'danger':
          return 'close'
        default:
          return null
      }
    } else {
      switch (props.type) {
        case 'info':
          return 'information-circled'
        case 'success':
          return 'checkmark'
        case 'warning':
          return 'warning-circled'
        case 'danger':
          return 'close-circled'
        default:
          return null
      }
    }
  })

  /**
   * @description Close the Message and emit events.
   */
  const close = () => {
    isActive.value = false
    emit('close')
    emit('update:active', false)
  }

  /**
   * @description Set timer to auto close message
   */
  const setAutoClose = () => {
    if (props.autoClose) {
      timer.value = setTimeout(() => {
        if (isActive.value) {
          close()
        }
      }, props.duration)
    }
  }

  onMounted(() => {
    setAutoClose()
  })

  watch(
    () => props.active,
    (value) => {
      /* istanbul ignore else */
      if (typeof value === 'boolean') {
        isActive.value = value
      }
    }
  )

  return {
    isActive,
    computedIcon,
    close,
    setAutoClose,
  }
}

export const messageProps = {
  /**
   * @type {Boolean}
   * @default true
   * @description Determines whether to show the message or hide it
   */
  active: {
    type: Boolean,
    default: true,
  },

  /**
   * @type {String}
   * @default undefined
   * @description The title of the message to display
   */
  title: String,

  closable: {
    type: Boolean,
    default: true,
  },

  /**
   * @type {String}
   * @default undefined
   * @description the message to display in the message component
   */
  message: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @default undefined
   * @description The type of message to display
   */
  type: {
    type: String,
    default: undefined,
  },

  /**
   * @type {boolean}
   * @default false
   * @description Determines if the icon should show in the message
   * component.
   */
  hasIcon: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String}
   * @default undefined
   * @description The size of the message to display
   */
  size: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @default undefined
   * @description The type of icon to display in the message
   */
  icon: String,

  /**
   * @type {String}
   * @default big
   * @description The default icon size to display in the message
   */
  iconSize: {
    type: String,
    default: 'big',
  },

  /**
   * @type {Boolean}
   * @default false
   * @description Determines if the message should auto close
   */
  autoClose: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Number}
   * @default: 3000
   * @description The timeout duration for the message if the
   * auto close is enabled.
   */
  duration: {
    type: Number,
    default: 3000,
  },
}

export const notificationProps = {
  /**
   * @type {Boolean}
   * @description Controls whether the component will render as an
   * epic alert rather than a standard one.
   */
  epic: Boolean,

  /**
   * @type {Object}
   * @prop {Function} validator - The validation method for the prop
   * @prop {String} type - The type of the property
   * @prop {String} default - The default value of the property
   * @description This controls what type of calert component to
   * render i.e danger/warning ect.
   */
  type: {
    validator: /* istanbul ignore next */ (v) => types.indexOf(v) >= 0,
    type: String,
    default: 'primary',
  },

  /**
   * @type {string}
   * @description Label for the close button, to be read by accessibility
   * screenreaders.
   */
  ariaCloseLabel: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Custom animation (transition name)
   */
  animation: {
    type: String,
    default: 'fade',
  },

  /**
   * @type {Boolean}
   * @description Default or large size notify
   */
  large: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Default or flex centered notify
   */
  center: {
    type: Boolean,
    default: false,
  },
}
